import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    PackageCheck, Building2, ChevronDown, Loader2, RefreshCw, Send, Eye, X,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchCostCenters,
    fetchIndents,
    fetchPreviewGrid,
    fetchExpDate,
    submitReceived,
    clearIndents,
    clearPreviewGrid,
    clearSubmitResult,
    resetOldStockReceived,
    selectOSRCCList,
    selectOSRIndentList,
    selectOSRPreviewItems,
    selectOSRExpDate,
    selectOSRSubmitResult,
    selectOSRLoading,
    selectOSRErrors,
} from '../../slices/purchaseSlice/oldStockReceivedSlice';

// ── Shared UI (mirrors NewStockReceived / DailyIssue) ───────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const formatDateForSP = (d) => {
    if (!d) return '';
    if (d instanceof Date) {
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    }
    return String(d);
};

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));
const isAssetCode = (code) => (code || '').trim().charAt(0) === '1';

const OldStockReceived = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userData?.employeeId || userData?.UID || 'system';
    const roleId = userData?.roleId || userData?.RID || '';
    const uid = userData?.UID || userData?.uid || '';

    const ccList        = useSelector(selectOSRCCList);
    const indentList    = useSelector(selectOSRIndentList);
    const previewItems  = useSelector(selectOSRPreviewItems);
    const expDate        = useSelector(selectOSRExpDate);
    const submitResult   = useSelector(selectOSRSubmitResult);
    const loading         = useSelector(selectOSRLoading);
    const errors           = useSelector(selectOSRErrors);

    const [selectedCC, setSelectedCC] = useState('');
    const [selectedIndent, setSelectedIndent] = useState('');
    const [checkedRows, setCheckedRows] = useState({});
    const [receivedDate, setReceivedDate] = useState(todayFormatted());
    const [remarks, setRemarks] = useState('');
    const [viewed, setViewed] = useState(false);

    useEffect(() => {
        if (uid && roleId) dispatch(fetchCostCenters({ uid, rid: roleId }));
        return () => dispatch(resetOldStockReceived());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset per-row checkbox state whenever the preview grid changes
    useEffect(() => {
        setCheckedRows({});
    }, [previewItems]);

    useEffect(() => {
        if (submitResult) {
            toast.success(`The Issue Received Ref No Is :-  ${submitResult.refNo}   Generated Successfully`);
            dispatch(clearSubmitResult());
            handleReset();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // ── Cost Center / Indent selection ──────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setSelectedIndent('');
        setViewed(false);
        dispatch(clearIndents());
        dispatch(clearPreviewGrid());
        if (cc) dispatch(fetchIndents(cc));
    };

    const handleIndentChange = (indent) => {
        setSelectedIndent(indent);
        setViewed(false);
        dispatch(clearPreviewGrid());
    };

    const handleView = async () => {
        if (!selectedCC)     { toast.error('Select CC/Job Code'); return; }
        if (!selectedIndent) { toast.error('Select Indent No'); return; }

        const result = await dispatch(fetchPreviewGrid({ ccode: selectedCC, indent: selectedIndent }));
        if (fetchPreviewGrid.fulfilled.match(result) && (result.payload?.length || 0) > 0) {
            setViewed(true);
            // Unlike New Stock Received, the legacy Old Stock Received page keeps the
            // pre-filled received date and remarks as-is after the preview loads.
            dispatch(fetchExpDate(selectedIndent));
        } else {
            setViewed(false);
        }
    };

    const handleClear = () => {
        setSelectedIndent('');
        setViewed(false);
        dispatch(clearPreviewGrid());
    };

    // ── Grid ─────────────────────────────────────────────────────────────────

    const toggleRowChecked = (idx) => setCheckedRows((prev) => ({ ...prev, [idx]: !prev[idx] }));

    const totalAmount = previewItems.reduce((s, i) => s + num(i.Amount), 0);

    // ── Submit ───────────────────────────────────────────────────────────────

    const handleSubmit = () => {
        if (!selectedCC)     { toast.error('Select CC/Job Code'); return; }
        if (!selectedIndent) { toast.error('Select Indent No'); return; }
        if (!receivedDate)   { toast.error('Select Date'); return; }
        if (!remarks.trim()) { toast.error('Enter Remarks'); return; }
        if (previewItems.length === 0) { toast.error('Invalid'); return; }
        if (!previewItems.every((_, idx) => checkedRows[idx])) {
            toast.error('Please Verify Received Issue Old Items');
            return;
        }

        dispatch(submitReceived({
            SCCCode: selectedCC,
            SDate: formatDateForSP(receivedDate),
            SRemarks: remarks,
            SIndentno: selectedIndent,
            RoleID: String(roleId),
            Createdby: currentUser,
        }));
    };

    const handleReset = () => {
        setSelectedCC(''); setSelectedIndent('');
        setCheckedRows({});
        setReceivedDate(todayFormatted());
        setRemarks('');
        setViewed(false);
        dispatch(clearIndents());
        dispatch(clearPreviewGrid());
        if (uid && roleId) dispatch(fetchCostCenters({ uid, rid: roleId }));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const checkedCount = previewItems.filter((_, idx) => checkedRows[idx]).length;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <PackageCheck className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Old Stock Received</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Confirm receipt of old stock raised against an Indent.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Selection ────────────────────────────────────────────────── */}
            <SectionCard icon={Building2} title="Cost Center / Indent" accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>Receive CC/Job Code</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)} disabled={loading.ccList}>
                                <option value="">-Please Select-</option>
                                {ccList.map((cc) => (
                                    <option key={cc.CCID} value={cc.CCID}>{cc.CCVAL}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 min-w-[240px]">
                        <Label required>Indent No</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedIndent} onChange={(e) => handleIndentChange(e.target.value)}
                                disabled={!selectedCC || loading.indentList}>
                                <option value="">-Please Select-</option>
                                {indentList.map((i) => (
                                    <option key={i.Indentvalue} value={i.Indentvalue}>{i.Indenttext}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.indentList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleView} disabled={loading.previewGrid}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                            {loading.previewGrid ? <SpinnerIcon /> : <Eye className="w-4 h-4" />} View
                        </button>
                        <button onClick={handleClear}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            <X className="w-3.5 h-3.5" /> Clear
                        </button>
                    </div>
                </div>
            </SectionCard>

            {/* ── Preview grid ─────────────────────────────────────────────── */}
            {viewed && previewItems.length > 0 && (
                <SectionCard icon={PackageCheck} title="Old Issued Items"
                    subtitle={`${checkedCount} of ${previewItems.length} item${previewItems.length !== 1 ? 's' : ''} verified`}
                    accent="amber">
                    <div className="overflow-x-auto rounded-lg border border-amber-200 dark:border-amber-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-amber-600 to-orange-600 text-white">
                                    {['Chk', 'Item Code', 'Item Name', 'Specification', 'Issued Quantity', 'Amount', 'Depreciation in %'].map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {previewItems.map((item, idx) => {
                                    const asset = isAssetCode(item.Itemcode);
                                    return (
                                        <tr key={`${item.Itemcode}_${idx}`} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-amber-50/40 dark:bg-amber-900/10'}>
                                            <td className="px-2.5 py-2">
                                                <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                    checked={!!checkedRows[idx]} onChange={() => toggleRowChecked(idx)} />
                                            </td>
                                            <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{item.Itemcode}</td>
                                            <td className="px-2.5 py-2 text-gray-900 dark:text-white">{item.Itemname}</td>
                                            <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{item.Specification}</td>
                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{item.Quantity}</td>
                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{num(item.Amount).toFixed(2)}</td>
                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{asset ? '—' : (item.Deppercent ?? '—')}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-amber-200 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/20 font-semibold text-gray-700 dark:text-gray-200">
                                    <td colSpan={4} />
                                    <td className="px-2.5 py-2 text-right">Total:-</td>
                                    <td className="px-2.5 py-2 text-right">{totalAmount.toFixed(2)}</td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label required>Issue Received Date</Label>
                            <CustomDatePicker value={receivedDate} onChange={(d) => setReceivedDate(formatDateForSP(d))}
                                format="DD-MMM-YYYY" placeholder="Select Date" maxDate={new Date()} />
                        </div>
                        <div>
                            <Label>Expected Date</Label>
                            <input className={inputCls} value={expDate} readOnly disabled />
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter remarks…"
                                value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Reset
                        </button>
                        <button onClick={handleSubmit} disabled={loading.submit}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {loading.submit ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.submit ? 'Submitting…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default OldStockReceived;
